/* Add a bus, edit its details, remove it — the WebAddBus / WebRemoveBus
   boards (2026-10-28). Until now every "Add a bus" button opened the classic
   dashboard, which had no way to add one. */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ConsoleModal, fieldInput, fieldSelect, ModalButtons, ModalField } from './Modal';
import { plural, useAction, useToast } from './kit';
import type { Bus, FleetModel } from './data/model';
import {
  addBus, busDetailsErrors, busDetailsOf, BusDetailsInput, driverIdsOf, PLATE_HINT, PLATE_RE, plateKey,
  platePretty, removeBus, rosterOf, saveBusDetails,
} from './data/busActions';
import { backOnRoad, OffReason, parkedOf, takeOffRoad } from './data/busStatus';

export const AddBusModal: React.FC<{ f: FleetModel; onClose: () => void; onAdded?: (plate: string) => void }> = ({ f, onClose, onAdded }) => {
  const { currentUser } = useAuth();
  const { toast } = useToast();
  const [plate, setPlate] = useState('');
  const [seats, setSeats] = useState('');
  const [makeModel, setMakeModel] = useState('');
  const [driverId, setDriverId] = useState('');
  const [touched, setTouched] = useState(false);
  const [serverErr, setServerErr] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const key = plateKey(plate);
  const plateErr = serverErr || (touched && !PLATE_RE.test(key) ? PLATE_HINT : null);
  const seatsErr = seats.trim() && !(parseInt(seats, 10) > 0 && parseInt(seats, 10) < 100) ? 'Seats from 1 to 99.' : null;
  const drivers = useMemo(() => [...f.drivers].sort((a, b) => a.name.localeCompare(b.name)), [f.drivers]);

  const submit = () => {
    setTouched(true);
    setServerErr(null);
    if (!PLATE_RE.test(key) || seatsErr || busy || !currentUser) return;
    setBusy(true);
    addBus(f, currentUser.uid, { plate, seats, makeModel, driverId: driverId || null })
      .then((r) => {
        if (!r.ok) { setServerErr(r.message); return; }
        toast({ kind: 'success', text: r.revived ? `${platePretty(r.plate)} is back in your fleet` : `${platePretty(r.plate)} added` });
        onAdded?.(r.plate);
        onClose();
      })
      .catch(() => toast({ kind: 'error', text: 'Couldn’t add the bus. Try again in a minute.' }))
      .finally(() => setBusy(false));
  };

  return (
    <ConsoleModal
      title="Add a bus"
      onClose={onClose}
      onSubmit={submit}
      enterHint="add"
      footer={<ModalButtons onCancel={onClose} primary="Add bus" onPrimary={submit} busy={busy} />}
    >
      <ModalField label="Number plate" error={plateErr}>
        {(id, ring) => (
          <input id={id} {...ring} style={{ ...fieldInput, textTransform: 'uppercase' }} value={plate} placeholder="KDA 442K" autoComplete="off" spellCheck={false}
            aria-invalid={!!plateErr} onChange={(e) => { setPlate(e.target.value); setServerErr(null); }} onBlur={() => { ring.onBlur(); if (plate.trim()) setTouched(true); }} />
        )}
      </ModalField>
      <div style={{ display: 'flex', gap: 12 }}>
        <ModalField label="Seats" width={140} error={seatsErr}>
          {(id, ring) => <input id={id} {...ring} style={fieldInput} inputMode="numeric" value={seats} placeholder="33" onChange={(e) => setSeats(e.target.value.replace(/\D/g, '').slice(0, 2))} />}
        </ModalField>
        <ModalField label="Make and model">
          {(id, ring) => <input id={id} {...ring} style={fieldInput} value={makeModel} placeholder="Toyota Coaster" onChange={(e) => setMakeModel(e.target.value)} />}
        </ModalField>
      </div>
      <ModalField label="Driver" hint={driverId && f.drivers.find((d) => d.id === driverId)?.busId ? 'They move off the bus they drive now.' : undefined}>
        {(id, ring) => (
          <select id={id} {...ring} style={{ ...fieldSelect, color: driverId ? 'var(--ink)' : 'var(--muted)' }} value={driverId} onChange={(e) => setDriverId(e.target.value)}>
            <option value="">Choose later</option>
            {drivers.map((d) => (
              <option key={d.id} value={d.id}>{d.name}{d.busName ? ` — now on ${d.busName}` : ''}</option>
            ))}
          </select>
        )}
      </ModalField>
    </ConsoleModal>
  );
};

export const EditBusModal: React.FC<{ bus: Bus; onClose: () => void; focus?: 'odometer' }> = ({ bus, onClose, focus }) => {
  const action = useAction();
  const [opened] = useState<BusDetailsInput>(() => busDetailsOf(bus));
  const [d, setD] = useState<BusDetailsInput>(opened);
  const odoRef = useRef<HTMLInputElement>(null);
  // After the dialog's own first-field focus, as MaintModal does.
  useEffect(() => { if (focus === 'odometer') odoRef.current?.focus(); }, [focus]);
  const [busy, setBusy] = useState(false);
  const errs = busDetailsErrors(d);
  const set = (k: keyof BusDetailsInput) => (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setD((x) => ({ ...x, [k]: e.target.value }));

  const submit = () => {
    if (Object.keys(errs).length || busy) return;
    setBusy(true);
    void action({
      busy: 'Saving…',
      done: `${bus.name} saved`,
      fail: 'Couldn’t save. Try again in a minute.',
      run: () => saveBusDetails(bus.id, d, opened),
    }).then((r) => { setBusy(false); if (r !== undefined) onClose(); });
  };

  return (
    <ConsoleModal title={`Edit ${bus.name}`} onClose={onClose} onSubmit={submit} enterHint="save"
      footer={<ModalButtons onCancel={onClose} primary="Save" onPrimary={submit} busy={busy} disabled={Object.keys(errs).length > 0} />}>
      <p style={{ margin: 0, color: 'var(--muted)', fontSize: 15 }}>The fuel checks use these. Leave a box empty if you don’t know it.</p>
      <div style={{ display: 'flex', gap: 12 }}>
        <ModalField label="Tank (litres)" error={errs.tank}>
          {(id, ring) => <input id={id} {...ring} style={fieldInput} inputMode="decimal" value={d.tank} placeholder="120" onChange={set('tank')} />}
        </ModalField>
        <ModalField label="Km per litre, normal" error={errs.benchmark}>
          {(id, ring) => <input id={id} {...ring} style={fieldInput} inputMode="decimal" value={d.benchmark} placeholder="6.5" onChange={set('benchmark')} />}
        </ModalField>
      </div>
      <div style={{ display: 'flex', gap: 12 }}>
        <ModalField label="Year made" error={errs.year}>
          {(id, ring) => <input id={id} {...ring} style={fieldInput} inputMode="numeric" value={d.year} placeholder="2016" onChange={set('year')} />}
        </ModalField>
        <ModalField label="Odometer counts in">
          {(id, ring) => (
            <select id={id} {...ring} style={fieldSelect} value={d.unit} onChange={set('unit')}>
              <option value="km">Kilometres</option>
              <option value="miles">Miles</option>
            </select>
          )}
        </ModalField>
      </div>
      <ModalField label="Odometer reading now" error={errs.odometer}
        hint={opened.odometer ? `${Number(opened.odometer).toLocaleString('en-GB')} on file. Change it only if that is wrong — each refill updates it.` : 'Km per litre needs a starting reading. The driver’s next refill also sets it.'}>
        {(id, ring) => <input id={id} {...ring} ref={odoRef} style={fieldInput} inputMode="numeric" value={d.odometer} placeholder="118400" onChange={(e) => setD((x) => ({ ...x, odometer: e.target.value.replace(/[^\d]/g, '') }))} />}
      </ModalField>
    </ConsoleModal>
  );
};

/** The WebRemoveBus board: what stays, who is affected. */
export const RemoveBusModal: React.FC<{ f: FleetModel; bus: Bus; onClose: () => void; onRemoved?: () => void }> = ({ f, bus, onClose, onRemoved }) => {
  const action = useAction();
  const [busy, setBusy] = useState(false);
  const kids = rosterOf(bus).length;
  const driverNames = driverIdsOf(bus).map((id) => f.drivers.find((p) => p.id === id)?.name).filter(Boolean) as string[];

  const submit = () => {
    if (busy) return;
    setBusy(true);
    void action({
      busy: `Removing ${bus.name}…`,
      done: `${bus.name} removed from the fleet`,
      fail: `Couldn’t remove ${bus.name}. Try again in a minute.`,
      run: () => removeBus(bus),
    }).then((r) => { setBusy(false); if (r !== undefined) { onRemoved?.(); onClose(); } });
  };

  const who = driverNames.length === 0 ? '' : driverNames.length === 1 ? `${driverNames[0]} stays on as a driver, without a bus.` : `${driverNames.join(' and ')} stay on as drivers, without a bus.`;
  return (
    <ConsoleModal title={`Remove ${bus.plate ? platePretty(plateKey(bus.plate)) : bus.name}?`} onClose={onClose} width={520}
      footer={<ModalButtons cancel="Keep bus" onCancel={onClose} primary="Remove bus" onPrimary={submit} busy={busy} danger />}>
      <p style={{ margin: 0, fontSize: 17, lineHeight: '25px' }}>
        Its trips and fuel stay in your reports. {who}{' '}
        {kids > 0 ? <b>{kids === 1 ? '1 child needs' : `${kids} children need`} a new bus.</b> : 'No children ride it.'}
      </p>
    </ConsoleModal>
  );
};

/* ── Off the road, and back (2026-10-28) — data/busStatus.ts ── */

/** Maintenance or suspended; every child on the bus rides another bus meanwhile. */
export const OffRoadModal: React.FC<{ f: FleetModel; bus: Bus; onClose: () => void }> = ({ f, bus, onClose }) => {
  const action = useAction();
  const { currentUser } = useAuth();
  const [reason, setReason] = useState<OffReason>('maintenance');
  const kids = rosterOf(bus);
  const others = useMemo(() => f.buses.filter((b) => b.id !== bus.id && b.status !== 'off_road').sort((a, b) => a.name.localeCompare(b.name)), [f.buses, bus.id]);
  const [moves, setMoves] = useState<Record<string, string>>({});
  const [tried, setTried] = useState(false);
  const [busy, setBusy] = useState(false);
  const unset = kids.filter((k) => !moves[k.childId]).length;
  const load = (id: string) => (f.buses.find((b) => b.id === id)?.expected ?? 0) + kids.filter((k) => moves[k.childId] === id).length;
  const over = Object.values(moves).filter((v, i, a) => a.indexOf(v) === i)
    .map((id) => f.buses.find((b) => b.id === id)).filter((b): b is Bus => !!b && b.seats != null && load(b.id) > (b.seats as number));
  const parentName = (pid: string) => f.parents.find((p) => p.id === pid)?.name || '';

  const submit = () => {
    setTried(true);
    if (busy || unset) return;
    setBusy(true);
    void action({
      busy: `Taking ${bus.name} off the road…`,
      done: (r: { moved: number; told: boolean }) => (r.moved
        ? `${bus.name} is off the road. ${plural(r.moved, 'child', 'children')} moved${r.told ? ', and their parents were told' : ' — their parents could not be messaged, so tell them'}.`
        : `${bus.name} is off the road.`),
      fail: `Couldn’t take ${bus.name} off the road. Nothing was changed.`,
      run: () => takeOffRoad(f, bus, reason, moves, { uid: currentUser?.uid ?? null, email: currentUser?.email ?? null }),
    }).then((r) => { setBusy(false); if (r !== undefined) onClose(); });
  };

  return (
    <ConsoleModal title={`Take ${bus.name} off the road`} onClose={onClose} width={600}
      footer={<ModalButtons onCancel={onClose} primary="Take off the road" onPrimary={submit} busy={busy} disabled={(tried && unset > 0) || (kids.length > 0 && others.length === 0)} />}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>Why</span>
        <div className="seg" role="group" aria-label="Why">
          <button type="button" className={reason === 'maintenance' ? 'on' : ''} aria-pressed={reason === 'maintenance'} onClick={() => setReason('maintenance')}>Maintenance</button>
          <button type="button" className={reason === 'suspended' ? 'on' : ''} aria-pressed={reason === 'suspended'} onClick={() => setReason('suspended')}>Suspended</button>
        </div>
      </div>
      {kids.length === 0 ? (
        <p style={{ margin: 0, fontSize: 16, lineHeight: '24px' }}>No children ride it. It stops running until you put it back on the road; its driver keeps their account.</p>
      ) : others.length === 0 ? (
        <p style={{ margin: 0, fontSize: 16, lineHeight: '24px', color: 'var(--bad)' }}>{plural(kids.length, 'child rides', 'children ride')} it, and there is no other bus on the road to move them to. Add a bus first.</p>
      ) : (
        <>
          <p style={{ margin: 0, fontSize: 16, lineHeight: '24px' }}>
            {plural(kids.length, 'child rides', 'children ride')} it. Choose the bus each one rides meanwhile — they all come back when {bus.name} returns. Their parents get a message.
          </p>
          <ModalField label="Move everyone to">
            {(id, ring) => (
              <select id={id} {...ring} style={fieldSelect} value="" onChange={(e) => { const to = e.target.value; if (to) setMoves(Object.fromEntries(kids.map((k) => [k.childId, to]))); }}>
                <option value="">Choose a bus…</option>
                {others.map((b) => <option key={b.id} value={b.id}>{b.name}{b.seats != null ? ` · ${b.expected ?? 0} of ${b.seats} seats` : ''}</option>)}
              </select>
            )}
          </ModalField>
          <div style={{ borderRadius: 14, background: 'var(--s2)', maxHeight: 260, overflow: 'auto' }}>
            {kids.map((k, i) => (
              <div key={k.childId || i} className="row" style={{ gap: 10, padding: '8px 14px', borderTop: i ? '1px solid var(--line)' : undefined }}>
                <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
                  <b style={{ fontSize: 15 }}>{k.name || 'A child'}</b>
                  {k.parentId && parentName(k.parentId) && <span className="sm" style={{ color: 'var(--muted)' }}>{parentName(k.parentId)}</span>}
                </div>
                <select aria-label={`Bus for ${k.name}`} className="tin" style={{ height: 36, maxWidth: 220, boxShadow: tried && !moves[k.childId] ? 'inset 0 0 0 2px var(--bad)' : undefined }}
                  value={moves[k.childId] || ''} onChange={(e) => setMoves((m) => ({ ...m, [k.childId]: e.target.value }))}>
                  <option value="">Choose…</option>
                  {others.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </div>
            ))}
          </div>
          {tried && unset > 0 && <span role="alert" style={{ fontSize: 14, fontWeight: 600, color: 'var(--bad)' }}>Choose a bus for {plural(unset, 'more child', 'more children')}.</span>}
          {over.length > 0 && <span style={{ fontSize: 14, color: 'var(--warn)', fontWeight: 600 }}>{over.map((b) => `${b.name} will carry ${load(b.id)} of ${b.seats} seats`).join(' · ')}.</span>}
        </>
      )}
    </ConsoleModal>
  );
};

/** The bus runs again; the children it parked come back from the buses they went to. */
export const BackOnRoadModal: React.FC<{ f: FleetModel; bus: Bus; onClose: () => void }> = ({ f, bus, onClose }) => {
  const action = useAction();
  const { currentUser } = useAuth();
  const [busy, setBusy] = useState(false);
  const { kids } = parkedOf(bus);
  const submit = () => {
    if (busy) return;
    setBusy(true);
    void action({
      busy: `Putting ${bus.name} back on the road…`,
      done: (r: { restored: number; told: boolean }) => (r.restored
        ? `${bus.name} is back on the road with its ${plural(r.restored, 'child', 'children')}${r.told ? ', and their parents were told' : ' — tell their parents, the message could not be sent'}.`
        : `${bus.name} is back on the road.`),
      fail: `Couldn’t put ${bus.name} back on the road. Nothing was changed.`,
      run: () => backOnRoad(f, bus, { uid: currentUser?.uid ?? null, email: currentUser?.email ?? null }),
    }).then((r) => { setBusy(false); if (r !== undefined) onClose(); });
  };
  return (
    <ConsoleModal title={`Put ${bus.name} back on the road?`} onClose={onClose} width={520}
      footer={<ModalButtons onCancel={onClose} primary="Back on the road" onPrimary={submit} busy={busy} />}>
      <p style={{ margin: 0, fontSize: 16, lineHeight: '24px' }}>
        {kids.length
          ? `${plural(kids.length, 'child moves', 'children move')} back to it from the buses they rode meanwhile, and their parents get a message.`
          : 'It runs again from the next trip.'}
      </p>
    </ConsoleModal>
  );
};
